import React, { useState } from 'react';
import { Star, TrendingUp, Clock, DollarSign, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const stats = [{
  icon: DollarSign,
  value: '£2.3M+',
  label: 'Extra Revenue Secured'
}, {
  icon: Clock,
  value: '100k+',
  label: 'Hours Saved'
}, {
  icon: TrendingUp,
  value: '10x',
  label: 'Lead Conversion Rate'
}, {
  icon: Star,
  value: '100%',
  label: 'Client Satisfaction'
}];
const testimonials = [{
  quote: 'We stopped missing calls and started capturing every lead. The ROI was immediate. I finally have my weekends back.',
  author: 'Sarah M.',
  role: 'Dental Practice Owner',
  metric: '300% Booking Increase',
  avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face'
}, {
  quote: "The automations handle all the follow-ups I used to forget. I'm closing more deals while working fewer hours.",
  author: 'Marcus T.',
  role: 'Real Estate Agent',
  metric: '20hrs/week Saved',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
}, {
  quote: 'Our new website and chat widget completely transformed how we get clients. It just works in the background.',
  author: 'Jennifer K.',
  role: 'Law Firm Partner',
  metric: '500% Lead Growth',
  avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face'
}, {
  quote: 'Online orders doubled in the first month. The automated review system keeps our Google rating at 4.9 stars.',
  author: 'James W.',
  role: 'Restaurant Owner',
  metric: '2x Online Orders',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'
}, {
  quote: 'The booking system alone saved us from hiring another receptionist. Patients love the automated reminders.',
  author: 'Priya S.',
  role: 'Physiotherapy Clinic Owner',
  metric: '45% No-Show Reduction',
  avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face'
}, {
  quote: 'Missed call text-back changed everything. We capture jobs that used to go to competitors when we were on site.',
  author: 'David R.',
  role: 'Plumbing & HVAC Owner',
  metric: '40+ Extra Jobs/Month',
  avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face'
}, {
  quote: 'The CRM tracks every guest inquiry automatically. Our direct bookings are up and we rely less on third-party sites.',
  author: 'Elena V.',
  role: 'Boutique Hotel Manager',
  metric: '35% More Direct Bookings',
  avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face'
}, {
  quote: 'Customers get instant texts when their car is ready. The review automation built our reputation fast.',
  author: 'Michael O.',
  role: 'Auto Repair Shop Owner',
  metric: '150+ New 5-Star Reviews',
  avatar: 'https://images.unsplash.com/photo-1506277886164-e25aa3f4ef7f?w=150&h=150&fit=crop&crop=face'
}, {
  quote: 'During tax season we used to drown in inquiries. Now the AI chatbot handles initial questions and books consultations for us.',
  author: 'Charlotte B.',
  role: 'Accounting Firm Director',
  metric: '60% Fewer Admin Hours',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face'
}];
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0
  })
};
export function ResultsSection() {
  const [currentPage, setCurrentPage] = useState(0);
  const [direction, setDirection] = useState(0);
  const itemsPerPage = 3;
  const totalPages = Math.ceil(testimonials.length / itemsPerPage);
  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setDirection(1);
      setCurrentPage(prev => prev + 1);
    }
  };
  const handlePrev = () => {
    if (currentPage > 0) {
      setDirection(-1);
      setCurrentPage(prev => prev - 1);
    }
  };
  const visibleTestimonials = testimonials.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);
  return <section id="results" className="py-16 md:py-24 gradient-section-warm relative" data-id="element-346">
      <div className="container mx-auto px-4" data-id="element-347">
        <div className="text-center mb-10 md:mb-16" data-id="element-348">
          <h2 className="text-3xl md:text-4xl font-black text-black mb-3 md:mb-4" data-id="element-349">
            REAL RESULTS. REAL FAST.
          </h2>
          <p className="text-lg md:text-xl text-gray-600" data-id="element-350">
            Businesses like yours are already saving time and making more money.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 mb-16 md:mb-24" data-id="element-351">
          {stats.map((stat, index) => <motion.div key={index} initial={{
          opacity: 0,
          scale: 0.8
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: index * 0.1,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} className="text-center group" data-id="element-352">
              <div className="flex justify-center mb-2 md:mb-4" data-id="element-353">
                <stat.icon className="w-6 h-6 md:w-8 md:h-8 text-primary" data-id="element-354" />
              </div>
              <div className="text-2xl md:text-5xl font-black text-black mb-1 md:mb-2" data-id="element-355">
                {stat.value}
              </div>
              <div className="text-gray-500 text-[10px] md:text-sm uppercase tracking-wider" data-id="element-356">
                {stat.label}
              </div>
            </motion.div>)}
        </div>

        {/* Desktop Testimonials Grid (Hidden on Mobile) */}
        <div className="hidden md:grid md:grid-cols-3 gap-8" data-id="element-357">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: i % 3 * 0.2,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} className="bg-white p-8 rounded-2xl border-l-4 border-primary relative flex flex-col" data-id="element-358">
              <div className="flex text-primary mb-4" data-id="element-359">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" data-id="element-360" />)}
              </div>
              <p className="text-black text-lg font-medium mb-6 flex-grow" data-id="element-361">
                "{t.quote}"
              </p>
              <div className="border-t border-gray-200 pt-4 flex items-center gap-4 mt-auto" data-id="element-362">
                <img src={t.avatar} alt={t.author} className="w-12 h-12 rounded-full object-cover flex-shrink-0 border-2 border-primary/20" data-id="element-363" />
                <div data-id="element-364">
                  <p className="text-black font-bold" data-id="element-365">{t.author}</p>
                  <p className="text-gray-500 text-sm mb-1" data-id="element-366">{t.role}</p>
                  <p className="text-primary text-sm font-bold" data-id="element-367">{t.metric}</p>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Mobile Testimonials Carousel (Hidden on Desktop) */}
        <div className="md:hidden flex flex-col items-center" data-id="element-368">
          <div className="w-full overflow-hidden relative" data-id="element-369">
            <AnimatePresence mode="wait" custom={direction} data-id="element-370">
              <motion.div key={currentPage} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 300,
                damping: 30
              },
              opacity: {
                duration: 0.2
              }
            }} className="space-y-4 w-full" data-id="element-371">
                {visibleTestimonials.map((t, i) => <div key={i} className="bg-white p-5 rounded-xl border-l-4 border-primary relative flex flex-col" data-id="element-372">
                    <div className="flex text-primary mb-3" data-id="element-373">
                      {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" data-id="element-374" />)}
                    </div>
                    <p className="text-black text-sm md:text-base font-medium mb-4 flex-grow" data-id="element-375">
                      "{t.quote}"
                    </p>
                    <div className="border-t border-gray-200 pt-3 flex items-center gap-3 mt-auto" data-id="element-376">
                      <img src={t.avatar} alt={t.author} className="w-10 h-10 rounded-full object-cover flex-shrink-0 border-2 border-primary/20" data-id="element-377" />
                      <div data-id="element-378">
                        <p className="text-black font-bold text-sm" data-id="element-379">
                          {t.author}
                        </p>
                        <p className="text-gray-500 text-xs mb-0.5" data-id="element-380">{t.role}</p>
                        <p className="text-primary text-xs font-bold" data-id="element-381">
                          {t.metric}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Carousel Controls */}
          <div className="flex items-center justify-center gap-4 md:gap-6 mt-6 md:mt-8" data-id="element-382">
            <button onClick={handlePrev} disabled={currentPage === 0} className={`w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center bg-primary/10 border border-primary/20 text-primary transition-all ${currentPage === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-primary hover:text-white'}`} aria-label="Previous reviews" data-id="element-383">
              <ChevronLeft size={18} data-id="element-384" />
            </button>

            <div className="flex items-center gap-1.5 md:gap-2" data-id="element-385">
              {[...Array(totalPages)].map((_, idx) => <div key={idx} className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full transition-all duration-300 ${currentPage === idx ? 'bg-primary w-3 md:w-4' : 'bg-gray-300'}`} data-id="element-386" />)}
            </div>

            <button onClick={handleNext} disabled={currentPage === totalPages - 1} className={`w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center bg-primary/10 border border-primary/20 text-primary transition-all ${currentPage === totalPages - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-primary hover:text-white'}`} aria-label="Next reviews" data-id="element-387">
              <ChevronRight size={18} data-id="element-388" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}